import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Scissors, Sparkles, Trash2, Pencil, Plus, Calculator, RotateCw } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";

const SERVICE_TYPES = {
  washing: "Washing",
  washing_zero_trim: "Washing + Zero Trim",
  washing_cutting: "Washing + Cutting",
  addon: "Add-on",
  spa: "Spa",
  pickup_drop: "Pickup & Drop",
};
const AGE_BRACKETS = ["0-2m", "3-4m", "5-6m", "7m+"];
const COATS = ["long", "short", "thick"];
const SIZES = ["mini", "med", "big", "giant"];
const UNITS = ["per_visit", "per_hour", "per_km"];

export default function Grooming() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canEdit = ["admin", "manager"].includes(user?.role);
  const [rates, setRates] = useState([]);
  const [editing, setEditing] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [open, setOpen] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);

  const load = () => api.get("/grooming/rates").then((r) => setRates(r.data));
  useEffect(() => { load(); }, []);

  const byKind = (species, types) =>
    rates.filter((r) => r.species === species && types.includes(r.service_type));

  const openCreate = (presets = null) => {
    setEditing(null);
    setDefaults(presets);
    setOpen(true);
  };
  const openEdit = (r) => { setEditing(r); setDefaults(null); setOpen(true); };

  const seed = async () => {
    if (!window.confirm("Seed the catalog with the Furmigo standard menu? Existing rates will be kept; only missing rows are added.")) return;
    try {
      const { data } = await api.post("/grooming/seed-defaults");
      toast.success(`Seeded — added ${data.inserted}, skipped ${data.skipped} (already present)`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const del = async (r) => {
    if (!window.confirm(`Delete rate "${r.label}"?`)) return;
    try {
      await api.delete(`/grooming/rates/${r.id}`);
      toast.success("Rate deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-5" data-testid="grooming-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Services</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight mt-1 flex items-center gap-2">
            <Scissors className="w-7 h-7 text-primary" />Grooming Catalog
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Rate matrix for cat & dog grooming. Used by POS via the configurator.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setQuoteOpen(true)} data-testid="grooming-configurator-btn">
            <Calculator className="w-4 h-4 mr-1" />Configurator
          </Button>
          {isAdmin && (
            <Button variant="outline" onClick={seed} data-testid="grooming-seed-btn">
              <RotateCw className="w-4 h-4 mr-1" />Seed standard menu
            </Button>
          )}
          {canEdit && (
            <Button onClick={() => openCreate()} data-testid="grooming-add-rate">
              <Plus className="w-4 h-4 mr-1" />Add rate
            </Button>
          )}
        </div>
      </div>

      <Tabs defaultValue="cat">
        <TabsList>
          <TabsTrigger value="cat" data-testid="tab-cat">Cat</TabsTrigger>
          <TabsTrigger value="dog" data-testid="tab-dog">Dog</TabsTrigger>
        </TabsList>

        {["cat", "dog"].map((species) => (
          <TabsContent key={species} value={species} className="space-y-4 mt-4">
            <Section
              title="Main services"
              section="main"
              species={species}
              rows={byKind(species, ["washing", "washing_zero_trim", "washing_cutting"])}
              canEdit={canEdit}
              onEdit={openEdit}
              onDelete={del}
              onAdd={openCreate}
            />
            <Section
              title="Add-ons"
              section="addon"
              species={species}
              rows={byKind(species, ["addon"])}
              canEdit={canEdit}
              onEdit={openEdit}
              onDelete={del}
              onAdd={openCreate}
            />
            <Section
              title="Spa / Pickup"
              section="spa_pickup"
              species={species}
              rows={byKind(species, ["spa", "pickup_drop"])}
              canEdit={canEdit}
              onEdit={openEdit}
              onDelete={del}
              onAdd={openCreate}
            />
          </TabsContent>
        ))}
      </Tabs>

      <RateDialog
        open={open}
        onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setDefaults(null); } }}
        item={editing}
        defaults={defaults}
        onSaved={load}
      />
      <ConfiguratorDialog open={quoteOpen} onOpenChange={setQuoteOpen} rates={rates} />
    </div>
  );
}

const SECTION_DEFAULTS = {
  main: { service_type: "washing", unit: "per_visit", label_hint: "e.g. Dog · Washing · Long Big 5-6m" },
  addon: { service_type: "addon", unit: "per_visit", label_hint: "e.g. Nail Cutting" },
  spa_pickup: { service_type: "spa", unit: "per_visit", label_hint: "e.g. Oil Massage / Pickup & Drop" },
};

function Section({ title, section, species, rows, canEdit, onEdit, onDelete, onAdd }) {
  const sectionDefaults = SECTION_DEFAULTS[section];
  return (
    <Card className="p-4" data-testid={`grooming-section-${section}-${species}`}>
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="font-heading text-lg font-semibold">
          {title}
          <span className="text-muted-foreground text-sm font-normal ml-1">({rows.length})</span>
        </div>
        {canEdit && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => onAdd({ ...sectionDefaults, species })}
            data-testid={`grooming-add-${section}-${species}`}
          >
            <Plus className="w-3.5 h-3.5 mr-1" />Add to {title.toLowerCase()}
          </Button>
        )}
      </div>
      {rows.length === 0 ? (
        <div className="text-sm text-muted-foreground py-6 text-center border border-dashed border-border rounded-md">
          No rates yet. Click <strong>Add to {title.toLowerCase()}</strong> to create the first one.
        </div>
      ) : (
        <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Label</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Age</TableHead>
            <TableHead>Coat</TableHead>
            <TableHead>Size</TableHead>
            <TableHead className="text-right">Price</TableHead>
            <TableHead>Unit</TableHead>
            <TableHead className="text-right"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.id} data-testid={`grooming-rate-${r.id}`}>
              <TableCell className="font-medium">{r.label}</TableCell>
              <TableCell className="text-xs uppercase tracking-wider text-muted-foreground">{SERVICE_TYPES[r.service_type] || r.service_type}</TableCell>
              <TableCell>{r.age_bracket || "—"}</TableCell>
              <TableCell>{r.coat_type || "—"}</TableCell>
              <TableCell>{r.size || "—"}</TableCell>
              <TableCell className="text-right font-mono">₹{r.price.toFixed(2)}</TableCell>
              <TableCell className="text-xs">{r.unit.replace("_", " ")}</TableCell>
              <TableCell className="text-right">
                {canEdit && (
                  <>
                    <Button variant="ghost" size="sm" onClick={() => onEdit(r)} data-testid={`grooming-edit-${r.id}`}><Pencil className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => onDelete(r)} className="text-destructive hover:text-destructive" data-testid={`grooming-delete-${r.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      )}
    </Card>
  );
}

function RateDialog({ open, onOpenChange, item, defaults, onSaved }) {
  const blank = { species: "dog", service_type: "washing", age_bracket: "", coat_type: "", size: "", label: "", price: 0, unit: "per_visit", gst_percent: 18, is_active: true };
  const [form, setForm] = useState(blank);

  useEffect(() => {
    if (item) {
      setForm({ ...item, age_bracket: item.age_bracket || "", coat_type: item.coat_type || "", size: item.size || "" });
    } else if (defaults) {
      setForm({ ...blank, ...defaults });
    } else {
      setForm(blank);
    }
    // eslint-disable-next-line
  }, [item, defaults, open]);

  const save = async () => {
    const payload = { ...form };
    ["age_bracket", "coat_type", "size"].forEach((k) => { if (!payload[k]) delete payload[k]; });
    payload.price = parseFloat(payload.price) || 0;
    payload.gst_percent = parseFloat(payload.gst_percent) || 0;
    if (!payload.label?.trim()) { toast.error("Label required"); return; }
    try {
      if (item) await api.patch(`/grooming/rates/${item.id}`, payload);
      else await api.post("/grooming/rates", payload);
      toast.success("Saved");
      onOpenChange(false); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="grooming-rate-dialog">
        <DialogHeader><DialogTitle>{item ? "Edit rate" : "New rate"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Species</Label>
            <Select value={form.species} onValueChange={(v) => setForm((f) => ({ ...f, species: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="cat">Cat</SelectItem><SelectItem value="dog">Dog</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label>Service type</Label>
            <Select value={form.service_type} onValueChange={(v) => setForm((f) => ({ ...f, service_type: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(SERVICE_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Age bracket</Label>
            <Select value={form.age_bracket || "_none"} onValueChange={(v) => setForm((f) => ({ ...f, age_bracket: v === "_none" ? "" : v }))}>
              <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">—</SelectItem>
                {AGE_BRACKETS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Coat</Label>
            <Select value={form.coat_type || "_none"} onValueChange={(v) => setForm((f) => ({ ...f, coat_type: v === "_none" ? "" : v }))}>
              <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">—</SelectItem>
                {COATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Size</Label>
            <Select value={form.size || "_none"} onValueChange={(v) => setForm((f) => ({ ...f, size: v === "_none" ? "" : v }))}>
              <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">—</SelectItem>
                {SIZES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Unit</Label>
            <Select value={form.unit} onValueChange={(v) => setForm((f) => ({ ...f, unit: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u.replace("_", " ")}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="col-span-2"><Label>Display label</Label>
            <Input value={form.label} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))} placeholder="e.g. Dog · Washing · Mini 5-6m" data-testid="rate-label" /></div>
          <div><Label>Price (₹)</Label><Input type="number" value={form.price} onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))} data-testid="rate-price" /></div>
          <div><Label>GST %</Label><Input type="number" value={form.gst_percent} onChange={(e) => setForm((f) => ({ ...f, gst_percent: e.target.value }))} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} data-testid="rate-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ConfiguratorDialog({ open, onOpenChange, rates }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="grooming-configurator-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-primary" />Grooming Configurator</DialogTitle>
        </DialogHeader>
        <GroomingConfigurator rates={rates} />
      </DialogContent>
    </Dialog>
  );
}

/**
 * Stand-alone configurator widget. Used here (preview) and exported for POS.
 * onPriced({lines, subtotal, gst_amount, grand_total}) — called when staff clicks "Add to bill".
 */
export function GroomingConfigurator({ rates, onPriced }) {
  const [species, setSpecies] = useState("dog");
  const [serviceType, setServiceType] = useState("washing");
  const [age, setAge] = useState("");
  const [coat, setCoat] = useState("");
  const [size, setSize] = useState("");
  const [addons, setAddons] = useState([]); // ids
  const [distance, setDistance] = useState(0);
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);

  // Reset child fields when species/type changes
  useEffect(() => { setAge(""); setCoat(""); setSize(""); setQuote(null); }, [species, serviceType]);

  // Filter dropdown options to only show choices that EXIST in the rate matrix
  const matching = useMemo(
    () => rates.filter((r) => r.species === species && r.service_type === serviceType && r.is_active !== false),
    [rates, species, serviceType]
  );
  const availableAges = useMemo(() => Array.from(new Set(matching.map((r) => r.age_bracket).filter(Boolean))).sort(), [matching]);
  const availableCoats = useMemo(() => Array.from(new Set(matching.filter((r) => !age || r.age_bracket === age).map((r) => r.coat_type).filter(Boolean))), [matching, age]);
  const availableSizes = useMemo(() => Array.from(new Set(matching.filter((r) => (!age || r.age_bracket === age) && (!coat || r.coat_type === coat)).map((r) => r.size).filter(Boolean))), [matching, age, coat]);

  const availableAddons = useMemo(
    () => rates.filter((r) => r.species === species && r.service_type === "addon" && r.is_active !== false),
    [rates, species]
  );

  const compute = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/grooming/quote", {
        species, service_type: serviceType,
        age_bracket: age || null, coat_type: coat || null, size: size || null,
        addon_ids: addons, distance_km: distance > 0 ? parseFloat(distance) : null,
      });
      setQuote(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No matching rate found");
      setQuote(null);
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div><Label>Species</Label>
          <Select value={species} onValueChange={setSpecies}>
            <SelectTrigger data-testid="cfg-species"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="cat">Cat</SelectItem><SelectItem value="dog">Dog</SelectItem></SelectContent>
          </Select>
        </div>
        <div className="col-span-2"><Label>Service</Label>
          <Select value={serviceType} onValueChange={setServiceType}>
            <SelectTrigger data-testid="cfg-service"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="washing">Washing</SelectItem>
              <SelectItem value="washing_zero_trim">Washing + Zero Trim</SelectItem>
              <SelectItem value="washing_cutting">Washing + Cutting</SelectItem>
              <SelectItem value="spa">Spa</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {availableAges.length > 0 && (
          <div><Label>Age</Label>
            <Select value={age} onValueChange={setAge}>
              <SelectTrigger data-testid="cfg-age"><SelectValue placeholder="Select age" /></SelectTrigger>
              <SelectContent>{availableAges.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
        {availableCoats.length > 0 && (
          <div><Label>Coat</Label>
            <Select value={coat} onValueChange={setCoat}>
              <SelectTrigger data-testid="cfg-coat"><SelectValue placeholder="Coat type" /></SelectTrigger>
              <SelectContent>{availableCoats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
        {availableSizes.length > 0 && (
          <div><Label>Size</Label>
            <Select value={size} onValueChange={setSize}>
              <SelectTrigger data-testid="cfg-size"><SelectValue placeholder="Pet size" /></SelectTrigger>
              <SelectContent>{availableSizes.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
        <div className="col-span-2 sm:col-span-3">
          <Label>Pickup/Drop distance (km, 0 = walk-in)</Label>
          <Input type="number" min="0" value={distance} onChange={(e) => setDistance(e.target.value)} data-testid="cfg-distance" />
        </div>
      </div>

      {availableAddons.length > 0 && (
        <div>
          <Label>Add-ons</Label>
          <div className="flex flex-wrap gap-2 mt-1.5">
            {availableAddons.map((a) => {
              const active = addons.includes(a.id);
              return (
                <button
                  key={a.id} type="button"
                  className={`text-xs px-2 py-1 rounded border ${active ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border"}`}
                  onClick={() => setAddons((cur) => active ? cur.filter((x) => x !== a.id) : [...cur, a.id])}
                  data-testid={`cfg-addon-${a.id}`}
                >
                  {a.label.replace(/^.*?· /, "")} · ₹{a.price}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <Button onClick={compute} disabled={busy} className="w-full" data-testid="cfg-compute">
        <Calculator className="w-4 h-4 mr-1" />{busy ? "Calculating…" : "Calculate price"}
      </Button>

      {quote && (
        <Card className="p-4 bg-muted/30" data-testid="cfg-quote">
          <div className="space-y-1 text-sm">
            {quote.lines.map((ln, i) => (
              <div key={i} className="flex justify-between">
                <span>{ln.label} <span className="text-muted-foreground">× {ln.qty}</span></span>
                <span className="font-mono">₹{(ln.price * ln.qty).toFixed(2)}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-border mt-3 pt-2 space-y-1 text-sm">
            <Row label="Subtotal" value={quote.subtotal} />
            <Row label={`GST`} value={quote.gst_amount} />
            <Row label="Grand total" value={quote.grand_total} bold />
          </div>
          {onPriced && (
            <Button className="w-full mt-3" onClick={() => onPriced(quote)} data-testid="cfg-add-to-bill">Add to bill</Button>
          )}
        </Card>
      )}
    </div>
  );
}

function Row({ label, value, bold }) {
  return (
    <div className={`flex justify-between ${bold ? "font-semibold text-base" : ""}`}>
      <span>{label}</span>
      <span className="font-mono">₹{value.toFixed(2)}</span>
    </div>
  );
}
